
import { defineComponent, computed, ref, onMounted, onBeforeUnmount } from 'vue'
// notificationVM is an alias of vue.VNode
// import { EVENT_CODE } from '../../utils/aria'
// import { on, off } from '../../utils/dom'

import { EVENT_CODE } from '../../utils/aria'
import { on, off } from '../../utils/dom'
import { TypeMap } from './constant'

import type { PropType } from 'vue'
import type { NotificationVM, Position } from './notification.type'

// const TypeMap: any = {
//   success: 'success',
//   info: 'info',
//   warning: 'warning',
//   error: 'error'
// }

export default defineComponent({
  name: 'MsNotification',
  props: {
    customClass: { type: String, default: '' },
    dangerouslyUseHTMLString: { type: Boolean, default: false },
    duration: { type: Number, default: 4500 },
    iconClass: { type: String, default: '' },
    id: { type: String, default: '' },
    message: {
      type: [String, Object] as PropType<string | NotificationVM>,
      default: ''
    },
    offset: { type: Number, default: 0 },
    onClick: {
      type: Function as PropType<() => void>,
      default: () => {}
    },
    onClose: {
      type: Function as PropType<() => void>,
      required: true
    },
    position: {
      type: String as PropType<Position>,
      default: 'top-right'
    },
    showClose: { type: Boolean, default: true },
    title: { type: String, default: '' },
    type: { type: String, default: '' },
    zIndex: { type: Number, default: 0 },
    closeAfterClick: { type: Boolean, default: false }
  },
  emits: ['destroy'],

  setup (props) {
    const visible = ref(false)
    let timer: any = null

    const typeClass = computed(() => {
      const type = props.type
      return type && TypeMap[type] ? `el-icon-${TypeMap[type]}` : ''
    })

    const typeCls = computed(() => `notify-${TypeMap[props.type]}`)

    const iconCls = computed(() => `notify-icon-${props.iconClass}`)

    const horizontalClass = computed(() => {
      const position = props.position
      return position.indexOf('center') >= 0
        ? 'center'
        : position.indexOf('right') > 1
          ? 'right'
          : 'left'
    })

    const verticalProperty = computed(() => {
      return props.position.startsWith('top') ? 'top' : 'bottom'
    })

    const positionStyle = computed(() => {
      return {
        [verticalProperty.value]: `${props.offset}px`,
        zIndex: props.zIndex
      }
    })

    function startTimer () {
      if (props.duration > 0) {
        timer = setTimeout(() => {
          if (visible.value) {
            close()
          }
        }, props.duration)
      }
    }

    function clearTimer () {
      clearTimeout(timer)
      timer = null
    }

    // click notify and check close after show
    function onClickNotify () {
      props.onClick()
      if (props.closeAfterClick) {
        close()
      }
    }

    function close () {
      visible.value = false
    }

    function onKeydown ({ code }: KeyboardEvent) {
      if (code === EVENT_CODE.delete || code === EVENT_CODE.backspace) {
        clearTimer() // press delete/backspace clear timer
      } else if (code === EVENT_CODE.esc) {
        // press esc to close the notification
        if (visible.value) {
          close()
        }
      } else {
        startTimer() // resume timer
      }
    }

    // lifecycle
    onMounted(() => {
      startTimer()
      visible.value = true
      // eslint-disable-next-line no-undef
      on(document, 'keydown', onKeydown as EventListenerOrEventListenerObject)
    })

    onBeforeUnmount(() => {
      // eslint-disable-next-line no-undef
      off(document, 'keydown', onKeydown as EventListenerOrEventListenerObject)
    })

    return {
      horizontalClass,
      typeClass,
      positionStyle,
      visible,
      typeCls,
      iconCls,
      close,
      clearTimer,
      startTimer,
      onClickNotify
    }
  }
})
